import type { Ref } from 'vue'
import type { Cart, CartItem } from '~/composables/types/cart'
import type { EventAddOn, EventDescription } from '~/composables/types/event'
import {
  filterAddOnsByBookingType,
  migrateCartItemAddOnQuantities,
} from '~/utils/cartAddOnHelpers'
import { eventGetters } from '~/utils/eventGetters'

export const resolveStoreEventIdForAddOns = (item: CartItem): string => {
  if (item.eventDescriptionId) {
    return item.eventDescriptionId
  }
  const ev = item.event as string | EventDescription | undefined
  if (typeof ev === 'string' && ev.length > 0) {
    return ev
  }
  if (ev && typeof ev === 'object') {
    return eventGetters.getId(ev)
  }
  return ''
}

/** Unique key per cart line — same event with different dates must not share a catalog. */
export const getCartLineKeyForAddOns = (item: CartItem): string | null => {
  const eventDescriptionId = resolveStoreEventIdForAddOns(item)
  if (!eventDescriptionId || item.type !== 'EVENT') {
    return null
  }

  const bookingType = item.dateTypeToBeBooked ?? 'PUBLIC'
  return `${eventDescriptionId}:${item.id}:${bookingType}`
}

const buildAddOnsRequestUrl = (eventDescriptionId: string): string =>
  `v1/store/events/${eventDescriptionId}/add-ons?include=media`

const itemNeedsAddonCatalog = (item: CartItem): boolean =>
  item.type === 'EVENT' &&
  Boolean(getCartLineKeyForAddOns(item)) &&
  !item.availableAddOns?.length

export const applyCatalogToCartLine = (
  cart: Ref<Cart>,
  lineKey: string,
  catalog: EventAddOn[]
) => {
  cart.value.items.forEach((item) => {
    if (getCartLineKeyForAddOns(item) === lineKey) {
      item.availableAddOns = catalog
      migrateCartItemAddOnQuantities(item)
    }
  })
}

export const useCartAvailableAddOns = () => {
  const fetchAvailableAddOnsForItem = async (
    item: CartItem
  ): Promise<EventAddOn[]> => {
    const eventDescriptionId = resolveStoreEventIdForAddOns(item)
    if (!eventDescriptionId || item.type !== 'EVENT') {
      return []
    }

    const response = await kftApiRequest<EventAddOn[]>(
      buildAddOnsRequestUrl(eventDescriptionId),
      { method: 'GET' }
    )

    const isPrivate = item.dateTypeToBeBooked === 'PRIVATE'
    return filterAddOnsByBookingType(response.data || [], isPrivate)
  }

  const refreshAvailableAddOnsForItems = async (
    cart: Ref<Cart>,
    items: CartItem[]
  ) => {
    const targets = items.filter(itemNeedsAddonCatalog)
    if (!targets.length) {
      return
    }

    const lineKeys = [
      ...new Set(
        targets
          .map((item) => getCartLineKeyForAddOns(item))
          .filter((key): key is string => Boolean(key))
      ),
    ]

    await Promise.all(
      lineKeys.map(async (lineKey) => {
        try {
          const item = targets.find(
            (target) => getCartLineKeyForAddOns(target) === lineKey
          )
          if (!item) return

          const catalog = await fetchAvailableAddOnsForItem(item)
          applyCatalogToCartLine(cart, lineKey, catalog)
        } catch (error) {
          console.error(
            'Error fetching available add-ons for cart line:',
            lineKey,
            error
          )
        }
      })
    )

    cart.value = {
      ...cart.value,
      items: [...cart.value.items],
    }

    await nextTick()
  }

  const refreshAvailableAddOnsForCart = async (cart: Ref<Cart>) => {
    const eventItems = cart.value.items.filter(
      (item) => item.type === 'EVENT' && getCartLineKeyForAddOns(item)
    )

    if (!eventItems.length) {
      return
    }

    const needsCatalog = eventItems.filter(itemNeedsAddonCatalog)
    if (needsCatalog.length) {
      await refreshAvailableAddOnsForItems(cart, needsCatalog)
      return
    }

    // Re-migrate quantities when catalog already exists (e.g. ticket qty changed)
    eventItems.forEach((item) => migrateCartItemAddOnQuantities(item))
    cart.value = {
      ...cart.value,
      items: [...cart.value.items],
    }
  }

  return {
    fetchAvailableAddOnsForItem,
    refreshAvailableAddOnsForCart,
    refreshAvailableAddOnsForItems,
  }
}
